import { useEffect, useState } from "react";
import { Pill, Plus, Search, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ImportDialog from "@/components/ImportDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SPECIALTY_STYLES } from "@/components/MedicineSearch";

const TABS = ["All", "Gynecology", "General Medicine", "Endocrinology", "Gastroenterology", "Urology", "Dermatology", "Pediatrics", "ENT", "Cardiology", "Orthopedics", "Psychiatry", "Neurology", "Pulmonology", "Ophthalmology", "Dental"];
const FORMS = ["Tablet", "Capsule", "Syrup", "Suspension", "Dry Syrup", "Drops", "Injection", "Cream", "Ointment", "Gel", "Lotion", "Powder", "Sachet", "Inhaler", "Nasal Spray", "Eye Drops", "Ear Drops", "Vaginal Gel", "Vaginal Suppository", "Mouthwash", "Effervescent Tablet"];
const SPECIALTIES = TABS.slice(1);

const EMPTY_MED = { name: "", generic: "", form: "Tablet", strength: "", specialty: "Gynecology", usage: "" };

export default function Medicines() {
    const [meds, setMeds] = useState(null);
    const [q, setQ] = useState("");
    const [specialty, setSpecialty] = useState("Gynecology");
    const [showAdd, setShowAdd] = useState(false);
    const [form, setForm] = useState(EMPTY_MED);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/medicines", { params: { q, specialty, limit: 200 } });
            setMeds(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [q, specialty]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post("/medicines", form);
            toast.success(`${data.name} added`);
            setShowAdd(false);
            setForm(EMPTY_MED);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setSaving(false); }
    };

    const remove = async (m) => {
        if (!window.confirm(`Delete ${m.name}?`)) return;
        try {
            await api.delete(`/medicines/${m.id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="medicines-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Medicines</h1>
                    <p className="mt-1 text-base text-slate-500">Clinic medicine database — used in prescriptions</p>
                </div>
                <div className="flex gap-2">
                    <ImportDialog module="medicines" label="Import" />
                    <Button data-testid="add-medicine-btn" onClick={() => setShowAdd(true)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-2"><Plus className="h-4 w-4" /> Add Medicine</Button>
                </div>
            </div>

            <div className="relative max-w-xl">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                    data-testid="medicines-search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by brand or generic name..."
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none placeholder:text-slate-400"
                />
            </div>

            <div className="flex gap-1.5 flex-wrap" data-testid="medicines-specialty-tabs">
                {TABS.map((s) => (
                    <button
                        key={s}
                        data-testid={`meds-tab-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setSpecialty(s)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-150 ${specialty === s ? "bg-[#E06D1F] text-white border-[#E06D1F]" : (SPECIALTY_STYLES[s] || "bg-slate-50 text-slate-600 border-slate-200") + " hover:border-orange-300"}`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="medicines-table-card">
                {!meds ? (
                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
                ) : meds.length === 0 ? (
                    <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="medicines-empty">No medicines found.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                            <th className="px-5 py-3">Medicine</th><th className="px-5 py-3 hidden md:table-cell">Generic</th><th className="px-5 py-3 hidden sm:table-cell">Specialty</th><th className="px-5 py-3 hidden lg:table-cell">Usage</th><th className="px-5 py-3"></th>
                        </tr></thead>
                        <tbody>
                            {meds.map((m) => (
                                <tr key={m.id} data-testid={`med-row-${m.id}`} className="border-t border-slate-100 hover:bg-[#FFF4EC]/60 transition-colors duration-100">
                                    <td className="px-5 py-3">
                                        <p className="font-medium text-slate-900 flex items-center gap-2"><Pill className="h-3.5 w-3.5 text-[#E06D1F]" />{m.name}{m.added_by && <span className="rounded bg-amber-50 border border-amber-200 px-1.5 text-[10px] font-semibold text-amber-700">CUSTOM</span>}</p>
                                        <p className="text-xs text-slate-500">{m.form}{m.strength ? ` · ${m.strength}` : ""}</p>
                                    </td>
                                    <td className="px-5 py-3 hidden md:table-cell text-slate-600 text-xs">{m.generic || "—"}</td>
                                    <td className="px-5 py-3 hidden sm:table-cell"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${SPECIALTY_STYLES[m.specialty] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{m.specialty}</span></td>
                                    <td className="px-5 py-3 hidden lg:table-cell text-slate-500 text-xs max-w-[220px] truncate">{m.usage || "—"}</td>
                                    <td className="px-5 py-3 text-right">{m.added_by && <Button data-testid={`med-delete-${m.id}`} size="sm" variant="ghost" onClick={() => remove(m)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-lg" data-testid="add-medicine-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Add Custom Medicine</DialogTitle>
                        <DialogDescription>Add a medicine to your clinic database — it will appear in the prescription autocomplete.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5 sm:col-span-2"><Label>Brand / Name *</Label><Input data-testid="med-add-name-input" required value={form.name} onChange={set("name")} placeholder="e.g. Doxinate Plus" /></div>
                        <div className="space-y-1.5 sm:col-span-2"><Label>Generic Composition</Label><Input data-testid="med-add-generic-input" value={form.generic} onChange={set("generic")} placeholder="e.g. Doxylamine + Pyridoxine" /></div>
                        <div className="space-y-1.5">
                            <Label>Form</Label>
                            <select data-testid="med-add-form-select" value={form.form} onChange={set("form")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                {FORMS.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5"><Label>Strength</Label><Input data-testid="med-add-strength-input" value={form.strength} onChange={set("strength")} placeholder="e.g. 10/10 mg" /></div>
                        <div className="space-y-1.5">
                            <Label>Specialty</Label>
                            <select data-testid="med-add-specialty-select" value={form.specialty} onChange={set("specialty")} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 px-3.5 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none">
                                {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5"><Label>Common Usage</Label><Input data-testid="med-add-usage-input" value={form.usage} onChange={set("usage")} placeholder="e.g. Vomiting in pregnancy" /></div>
                        <Button data-testid="med-add-submit-btn" type="submit" disabled={saving} className="sm:col-span-2 bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Medicine</Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
